/**
 * Correspondence Domain Logic
 *
 * Pure functions for correspondence state management.
 * No external dependencies.
 *
 * State Machine:
 *   PRINTED ──send()───→ SENT
 *   PRINTED ──cancel()──→ CANCELLED
 *   SENT ────unsend()───→ PRINTED
 *   CANCELLED ─uncancel()→ PRINTED
 */

/**
 * Correspondence status constants.
 */
export const CorrespondenceStatus = Object.freeze({
  PRINTED: 'printed',
  CANCELLED: 'cancelled',
  SENT: 'sent'
});

/**
 * Check if a correspondence is in PRINTED status.
 *
 * @param {Object} data - Correspondence data
 * @param {string} data.status - Current status
 * @returns {boolean}
 */
export function isPrinted(data) {
  return data?.status === CorrespondenceStatus.PRINTED;
}

/**
 * Check if a correspondence is in SENT status.
 *
 * @param {Object} data - Correspondence data
 * @param {string} data.status - Current status
 * @returns {boolean}
 */
export function isSent(data) {
  return data?.status === CorrespondenceStatus.SENT;
}

/**
 * Check if a correspondence is in CANCELLED status.
 *
 * @param {Object} data - Correspondence data
 * @param {string} data.status - Current status
 * @returns {boolean}
 */
export function isCancelled(data) {
  return data?.status === CorrespondenceStatus.CANCELLED;
}

/**
 * Check if a transition is valid.
 *
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @returns {boolean}
 */
export function isValidTransition(fromStatus, toStatus) {
  if (fromStatus === CorrespondenceStatus.PRINTED) {
    return toStatus === CorrespondenceStatus.SENT ||
           toStatus === CorrespondenceStatus.CANCELLED;
  }
  if (fromStatus === CorrespondenceStatus.SENT) {
    return toStatus === CorrespondenceStatus.PRINTED;
  }
  if (fromStatus === CorrespondenceStatus.CANCELLED) {
    return toStatus === CorrespondenceStatus.PRINTED;
  }
  return false;
}

/**
 * Get allowed transitions from a given status.
 *
 * @param {string} status - Current status
 * @returns {string[]} Array of valid target statuses
 */
export function getAllowedTransitions(status) {
  switch (status) {
    case CorrespondenceStatus.PRINTED:
      return [CorrespondenceStatus.SENT, CorrespondenceStatus.CANCELLED];
    case CorrespondenceStatus.SENT:
      return [CorrespondenceStatus.PRINTED];
    case CorrespondenceStatus.CANCELLED:
      return [CorrespondenceStatus.PRINTED];
    default:
      return [];
  }
}

/**
 * Create initial correspondence data.
 *
 * @param {Object} params
 * @param {string} params.id - Unique identifier
 * @param {Date|string} params.dateCreated - Creation date
 * @param {string} params.createdByUser - User who created it
 * @param {string} [params.template] - Letter template identifier
 * @param {Object} [params.options] - Template options
 * @param {string} [params.listingId] - ID of reference listing
 * @returns {Object} Initial correspondence data
 */
export function createCorrespondenceData({
  id,
  dateCreated,
  createdByUser,
  template = null,
  options = {},
  listingId = null
}) {
  return {
    id,
    dateCreated,
    createdByUser,
    status: CorrespondenceStatus.PRINTED,
    template,
    options,
    listingId,
    dateSent: null,
    sentByUser: null,
    sendNote: null,
    dateCancelled: null,
    cancelledByUser: null,
    cancelNote: null
  };
}

/**
 * Transition correspondence to SENT status.
 * Only valid from PRINTED status.
 *
 * @param {Object} data - Current correspondence data
 * @param {Object} params
 * @param {Date|string} params.dateSent - Date sent
 * @param {string} params.sentByUser - User who sent it
 * @param {string} [params.note] - Optional note
 * @returns {{ success: boolean, data?: Object, error?: string }}
 */
export function transitionToSent(data, { dateSent, sentByUser, note = null }) {
  if (!data) {
    return { success: false, error: 'No correspondence data provided' };
  }

  if (!isPrinted(data)) {
    return {
      success: false,
      error: `Cannot send correspondence with status "${data.status}". Must be "${CorrespondenceStatus.PRINTED}".`
    };
  }

  return {
    success: true,
    data: {
      ...data,
      status: CorrespondenceStatus.SENT,
      dateSent,
      sentByUser,
      sendNote: note
    }
  };
}

/**
 * Transition correspondence from SENT back to PRINTED status.
 * Only valid from SENT status.
 *
 * @param {Object} data - Current correspondence data
 * @returns {{ success: boolean, data?: Object, error?: string }}
 */
export function transitionFromSent(data) {
  if (!data) {
    return { success: false, error: 'No correspondence data provided' };
  }

  if (!isSent(data)) {
    return {
      success: false,
      error: `Cannot unsend correspondence with status "${data.status}". Must be "${CorrespondenceStatus.SENT}".`
    };
  }

  return {
    success: true,
    data: {
      ...data,
      status: CorrespondenceStatus.PRINTED,
      dateSent: null,
      sentByUser: null,
      sendNote: null
    }
  };
}

/**
 * Transition correspondence to CANCELLED status.
 * Only valid from PRINTED status.
 *
 * @param {Object} data - Current correspondence data
 * @param {Object} params
 * @param {Date|string} params.dateCancelled - Date cancelled
 * @param {string} params.cancelledByUser - User who cancelled it
 * @param {string} [params.note] - Optional note
 * @returns {{ success: boolean, data?: Object, error?: string }}
 */
export function transitionToCancelled(data, { dateCancelled, cancelledByUser, note = null }) {
  if (!data) {
    return { success: false, error: 'No correspondence data provided' };
  }

  if (!isPrinted(data)) {
    return {
      success: false,
      error: `Cannot cancel correspondence with status "${data.status}". Must be "${CorrespondenceStatus.PRINTED}".`
    };
  }

  return {
    success: true,
    data: {
      ...data,
      status: CorrespondenceStatus.CANCELLED,
      dateCancelled,
      cancelledByUser,
      cancelNote: note
    }
  };
}

/**
 * Transition correspondence from CANCELLED back to PRINTED status.
 * Only valid from CANCELLED status.
 *
 * @param {Object} data - Current correspondence data
 * @returns {{ success: boolean, data?: Object, error?: string }}
 */
export function transitionFromCancelled(data) {
  if (!data) {
    return { success: false, error: 'No correspondence data provided' };
  }

  if (!isCancelled(data)) {
    return {
      success: false,
      error: `Cannot uncancel correspondence with status "${data.status}". Must be "${CorrespondenceStatus.CANCELLED}".`
    };
  }

  return {
    success: true,
    data: {
      ...data,
      status: CorrespondenceStatus.PRINTED,
      dateCancelled: null,
      cancelledByUser: null,
      cancelNote: null
    }
  };
}

/**
 * Sort correspondences by most recent activity.
 * Priority: SENT (by dateSent) > PRINTED (by dateCreated) > CANCELLED (by dateCancelled)
 *
 * @param {Object[]} correspondences - Array of correspondence data
 * @returns {Object[]} Sorted array (most recent first)
 */
export function sortByMostRecent(correspondences) {
  if (!correspondences || !Array.isArray(correspondences)) {
    return [];
  }

  return [...correspondences].sort((a, b) => {
    // Sent items come first
    if (isSent(a) && !isSent(b)) return -1;
    if (!isSent(a) && isSent(b)) return 1;

    // Among sent items, sort by dateSent
    if (isSent(a) && isSent(b)) {
      return compareDates(b.dateSent, a.dateSent);
    }

    // Printed items come before cancelled
    if (isPrinted(a) && isCancelled(b)) return -1;
    if (isCancelled(a) && isPrinted(b)) return 1;

    // Among printed items, sort by dateCreated
    if (isPrinted(a) && isPrinted(b)) {
      return compareDates(b.dateCreated, a.dateCreated);
    }

    // Among cancelled items, sort by dateCancelled
    if (isCancelled(a) && isCancelled(b)) {
      return compareDates(b.dateCancelled, a.dateCancelled);
    }

    // Fallback to dateCreated
    return compareDates(b.dateCreated, a.dateCreated);
  });
}

/**
 * Get the most recent correspondence from an array.
 *
 * @param {Object[]} correspondences - Array of correspondence data
 * @returns {Object|null} Most recent correspondence or null
 */
export function getMostRecent(correspondences) {
  const sorted = sortByMostRecent(correspondences);
  return sorted.length > 0 ? sorted[0] : null;
}

/**
 * Get the date of the most recent activity for a correspondence.
 *
 * @param {Object} data - Correspondence data
 * @returns {Date|string|null} The most relevant date
 */
export function getActivityDate(data) {
  if (!data) return null;

  if (isSent(data)) {
    return data.dateSent;
  }
  if (isCancelled(data)) {
    return data.dateCancelled;
  }
  return data.dateCreated;
}

/**
 * Filter correspondences by status.
 *
 * @param {Object[]} correspondences - Array of correspondence data
 * @param {string} status - Status to filter by
 * @returns {Object[]} Filtered array
 */
export function filterByStatus(correspondences, status) {
  if (!correspondences || !Array.isArray(correspondences)) {
    return [];
  }
  return correspondences.filter(c => c.status === status);
}

/**
 * Count correspondences by status.
 *
 * @param {Object[]} correspondences - Array of correspondence data
 * @returns {{ printed: number, sent: number, cancelled: number, total: number }}
 */
export function countByStatus(correspondences) {
  if (!correspondences || !Array.isArray(correspondences)) {
    return { printed: 0, sent: 0, cancelled: 0, total: 0 };
  }

  const counts = {
    printed: 0,
    sent: 0,
    cancelled: 0,
    total: correspondences.length
  };

  for (const c of correspondences) {
    if (isPrinted(c)) counts.printed++;
    else if (isSent(c)) counts.sent++;
    else if (isCancelled(c)) counts.cancelled++;
  }

  return counts;
}

/**
 * Append a correspondence to the list.
 *
 * @param {Object[]} correspondences
 * @param {Object} correspondence
 * @returns {Object[]} New array with correspondence appended
 */
export function appendCorrespondence(correspondences, correspondence) {
  if (!correspondence || !correspondence.id) {
    throw new TypeError('correspondence with id is required');
  }
  const list = Array.isArray(correspondences) ? correspondences : [];
  return [...list, correspondence];
}

/**
 * Replace a correspondence in the list by ID.
 *
 * @param {Object[]} correspondences
 * @param {string} correspondenceId
 * @param {Object} nextCorrespondence
 * @returns {{ correspondences: Object[], found: boolean }}
 */
export function replaceCorrespondence(correspondences, correspondenceId, nextCorrespondence) {
  if (!correspondenceId) {
    throw new TypeError('correspondenceId is required');
  }
  if (!nextCorrespondence || !nextCorrespondence.id) {
    throw new TypeError('nextCorrespondence with id is required');
  }

  const list = Array.isArray(correspondences) ? correspondences : [];
  let found = false;
  const updated = list.map(correspondence => {
    if (correspondence?.id === correspondenceId) {
      found = true;
      return nextCorrespondence;
    }
    return correspondence;
  });

  return {
    correspondences: updated,
    found
  };
}

/**
 * Remove a correspondence from the list by ID.
 *
 * @param {Object[]} correspondences
 * @param {string} correspondenceId
 * @returns {{ correspondences: Object[], removed: Object|null }}
 */
export function removeCorrespondence(correspondences, correspondenceId) {
  if (!correspondenceId) {
    throw new TypeError('correspondenceId is required');
  }

  const list = Array.isArray(correspondences) ? correspondences : [];
  let removed = null;
  const updated = list.filter(correspondence => {
    if (correspondence?.id === correspondenceId) {
      removed = correspondence;
      return false;
    }
    return true;
  });

  return {
    correspondences: updated,
    removed
  };
}

/**
 * Compare two dates for sorting (handles Date objects and strings).
 * @private
 */
function compareDates(a, b) {
  const dateA = a instanceof Date ? a.getTime() : new Date(a).getTime();
  const dateB = b instanceof Date ? b.getTime() : new Date(b).getTime();

  if (isNaN(dateA) && isNaN(dateB)) return 0;
  if (isNaN(dateA)) return 1;
  if (isNaN(dateB)) return -1;

  return dateA - dateB;
}

// ============================================================================
// Migration helpers (for migrating correspondences from listings to properties)
// ============================================================================

/**
 * Compute which correspondences need to be migrated.
 * Filters out correspondences that have already been migrated.
 *
 * @param {Object[]} listingCorrespondences - Correspondences on the listing
 * @param {string[]} alreadyMigratedIds - IDs that have already been migrated
 * @returns {Object[]} Correspondences that need migration
 */
export function computeCorrespondencesToMigrate(listingCorrespondences, alreadyMigratedIds = []) {
  if (!listingCorrespondences || !Array.isArray(listingCorrespondences)) {
    return [];
  }

  const migratedSet = new Set(alreadyMigratedIds || []);
  return listingCorrespondences.filter(c => c && c.id && !migratedSet.has(c.id));
}

/**
 * Merge correspondences into an existing array, avoiding duplicates by ID.
 *
 * @param {Object[]} existingCorrespondences - Current correspondences on the target
 * @param {Object[]} newCorrespondences - Correspondences to merge in
 * @returns {{ merged: Object[], addedIds: string[] }} Merged array and IDs that were added
 */
export function mergeCorrespondencesById(existingCorrespondences, newCorrespondences) {
  const existing = existingCorrespondences || [];
  const toAdd = newCorrespondences || [];

  const existingIds = new Set(existing.map(c => c.id));
  const addedIds = [];
  const merged = [...existing];

  for (const correspondence of toAdd) {
    if (correspondence && correspondence.id && !existingIds.has(correspondence.id)) {
      merged.push(correspondence);
      addedIds.push(correspondence.id);
      existingIds.add(correspondence.id);
    }
  }

  return { merged, addedIds };
}

/**
 * Compute migration analysis for a listing.
 * Pure function that determines what needs to be migrated without side effects.
 *
 * @param {Object} params
 * @param {Object[]} params.listingCorrespondences - Correspondences on the listing
 * @param {string[]} params.alreadyMigratedIds - IDs already migrated from this listing
 * @param {Object[]} params.propertyCorrespondences - Current correspondences on the property
 * @param {string|null} params.propertyId - The target property ID (null means no property linked)
 * @returns {{
 *   canMigrate: boolean,
 *   reason?: string,
 *   toMigrate: Object[],
 *   mergedCorrespondences: Object[],
 *   newlyMigratedIds: string[]
 * }}
 */
export function computeMigrationPlan({
  listingCorrespondences,
  alreadyMigratedIds,
  propertyCorrespondences,
  propertyId
}) {
  // Check if migration is possible
  if (!propertyId) {
    return {
      canMigrate: false,
      reason: 'no_property',
      toMigrate: [],
      mergedCorrespondences: [],
      newlyMigratedIds: []
    };
  }

  // Compute what needs to be migrated
  const toMigrate = computeCorrespondencesToMigrate(listingCorrespondences, alreadyMigratedIds);

  if (toMigrate.length === 0) {
    return {
      canMigrate: false,
      reason: 'nothing_to_migrate',
      toMigrate: [],
      mergedCorrespondences: propertyCorrespondences || [],
      newlyMigratedIds: []
    };
  }

  // Compute the merge result
  const { merged, addedIds } = mergeCorrespondencesById(propertyCorrespondences, toMigrate);

  if (addedIds.length === 0) {
    return {
      canMigrate: false,
      reason: 'already_on_property',
      toMigrate,
      mergedCorrespondences: merged,
      newlyMigratedIds: []
    };
  }

  return {
    canMigrate: true,
    toMigrate,
    mergedCorrespondences: merged,
    newlyMigratedIds: addedIds
  };
}

// ============================================================================
// Migration validation helpers
// ============================================================================

/**
 * Migration validation issue types.
 */
export const MigrationValidationIssue = Object.freeze({
  UNMIGRATED_CORRESPONDENCES: 'unmigrated_correspondences',
  MISSING_ON_PROPERTY: 'missing_on_property',
  NO_PROPERTY_LINKED: 'no_property_linked'
});

/**
 * Validate that a listing's correspondences have been properly migrated to its property.
 *
 * @param {Object} params
 * @param {Object[]} params.listingCorrespondences - Correspondences on the listing
 * @param {string[]} params.migratedIds - IDs recorded as migrated
 * @param {Object[]} params.propertyCorrespondences - Correspondences on the property
 * @param {string|null} params.propertyId - The property ID (null if not linked)
 * @returns {{
 *   valid: boolean,
 *   issues: Array<{ type: string, details?: Object }>
 * }}
 */
export function validateCorrespondenceMigration({
  listingCorrespondences,
  migratedIds,
  propertyCorrespondences,
  propertyId
}) {
  const issues = [];
  const listing = listingCorrespondences || [];
  const migrated = new Set(migratedIds || []);
  const property = propertyCorrespondences || [];
  const propertyIds = new Set(property.map(c => c.id));

  // Skip validation if no correspondences on listing
  if (listing.length === 0) {
    return { valid: true, issues: [] };
  }

  // Issue 1: Has correspondences but no property to migrate to
  if (listing.length > 0 && !propertyId) {
    issues.push({
      type: MigrationValidationIssue.NO_PROPERTY_LINKED,
      details: { correspondenceCount: listing.length }
    });
    return { valid: false, issues };
  }

  // Issue 2: Correspondences not recorded as migrated
  const unmigratedIds = listing
    .filter(c => c && c.id && !migrated.has(c.id))
    .map(c => c.id);

  if (unmigratedIds.length > 0) {
    issues.push({
      type: MigrationValidationIssue.UNMIGRATED_CORRESPONDENCES,
      details: { ids: unmigratedIds }
    });
  }

  // Issue 3: Migrated correspondences not found on property
  const missingOnProperty = listing
    .filter(c => c && c.id && migrated.has(c.id) && !propertyIds.has(c.id))
    .map(c => c.id);

  if (missingOnProperty.length > 0) {
    issues.push({
      type: MigrationValidationIssue.MISSING_ON_PROPERTY,
      details: { ids: missingOnProperty }
    });
  }

  return {
    valid: issues.length === 0,
    issues
  };
}
